import { Controller } from 'stimulus';

class AutocompleteController extends Controller {
  MOBILE_BREAKPOINT = 576;
  static targets = ['type', 'query', 'container']

  initialize() {
    this.search = this.debounce(this.search.bind(this), 500);
    this.currentQuery = '';
  }

  debounce(callback, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(this, args), wait);
    };
  }

  search() {
    const query = {q: this.queryTarget.value};

    if (this.currentQuery != query.q && query.q.length > 0) {
      this.currentQuery = query.q;

      if (this.data.get('type-id')) {
        query.type_id = this.data.get('type-id');
      }
      fetch(`${this.data.get('url')}?${new URLSearchParams(query)}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(response => response.text())
        .then(html => {
          this.containerTarget.innerHTML = html;
          this.showResults();
        })
        .catch(error => console.error('Error:', error));
    }
  }

  navigate(e) {
    switch (e.keyCode) {
      case 37: // left
        e.preventDefault();
        e.stopPropagation();
        this.navigateLeft();
        break;
      case 38: // up
        e.preventDefault();
        this.navigateUp();
        break;
      case 39: // right
        e.preventDefault();
        e.stopPropagation();
        this.navigateRight();
        break;
      case 40: // down
        e.preventDefault();
        this.navigateDown();
        break;
    }
  }

  navigateUp() {
    const items = this.visibleMenuItems();
    if (items.length == 0) {
      return;
    }

    const currentIndex = items.indexOf(document.activeElement);
    if (currentIndex > 0) {
      items[currentIndex - 1].focus();
    } else if (currentIndex == 0) {
      this.queryTarget.focus();
    } else {
      items[0].focus();
    }
  }

  navigateDown() {
    const pane = this.activePane();
    const items = this.visibleMenuItems();
    if (items.length == 0) {
      return;
    }

    const currentIndex = items.indexOf(document.activeElement);
    if (currentIndex > -1 && currentIndex < items.length - 1) {
      items[currentIndex + 1].focus();
    } else if (currentIndex == items.length - 1) {
      if (pane) {
        const nextPane = this.alternatePane(pane);
        const nextItems = this.paneItems(nextPane);
        if (nextItems.length > 0) {
          nextItems[0].focus();
          return;
        }
      }
      items[0].focus();
    } else {
      items[0].focus();
    }
  }

  navigateLeft() {
    this.focusAdjacentColumn('left');
  }

  navigateRight() {
    this.focusAdjacentColumn('right');
  }

  focusAdjacentColumn(direction) {
    const menu = this.dropdownMenu();
    if (!menu) {
      return;
    }

    const queryPane = menu.querySelector('[data-mobile-dropdown-menu-target="items"]');
    const areaPane = menu.querySelector('[data-mobile-dropdown-menu-target="areas"]');

    if (!queryPane || !areaPane) {
      return;
    }

    const activeElement = document.activeElement;
    const inQuery = queryPane.contains(activeElement);
    const inArea = areaPane.contains(activeElement);

    if (direction == 'right' && inQuery) {
      this.focusMatchingIndex(activeElement, queryPane, areaPane);
    } else if (direction == 'left' && inArea) {
      this.focusMatchingIndex(activeElement, areaPane, queryPane);
    }
  }

  focusMatchingIndex(referenceElement, fromPane, toPane) {
    const fromItems = this.paneItems(fromPane);
    const toItems = this.paneItems(toPane);

    if (toItems.length == 0) {
      return;
    }

    const currentIndex = fromItems.indexOf(referenceElement);
    if (currentIndex < 0) {
      toItems[0].focus();
      return;
    }

    const targetIndex = Math.min(currentIndex, toItems.length - 1);
    toItems[targetIndex].focus();
  }

  paneItems(pane) {
    if (!pane) {
      return [];
    }
    return [...pane.querySelectorAll('a.item, button.item, .dropdown-item')].filter((element) => this.isVisible(element));
  }

  alternatePane(pane) {
    const menu = this.dropdownMenu();
    if (!menu) {
      return null;
    }

    const itemsPane = menu.querySelector('[data-mobile-dropdown-menu-target="items"]');
    const areasPane = menu.querySelector('[data-mobile-dropdown-menu-target="areas"]');

    if (pane == itemsPane) {
      return areasPane;
    }

    if (pane == areasPane) {
      return itemsPane;
    }

    return null;
  }

  visibleMenuItems() {
    const pane = this.activePane();
    if (pane) {
      const paneItems = this.paneItems(pane);
      if (paneItems.length > 0) {
        return paneItems;
      }
    }

    const menu = this.dropdownMenu();
    if (menu) {
      return [...menu.getElementsByClassName('dropdown-item')].filter((element) => this.isVisible(element));
    }

    return [];
  }

  dropdownMenu() {
    return this.containerTarget.querySelector('.dropdown-menu');
  }

  activePane() {
    const menu = this.dropdownMenu();
    if (!menu) {
      return null;
    }

    const itemsPane = menu.querySelector('[data-mobile-dropdown-menu-target="items"]');
    const areasPane = menu.querySelector('[data-mobile-dropdown-menu-target="areas"]');

    if (itemsPane && itemsPane.contains(document.activeElement)) {
      return itemsPane;
    }

    if (areasPane && areasPane.contains(document.activeElement)) {
      return areasPane;
    }

    const itemsTab = menu.querySelector('[data-mobile-dropdown-menu-target="itemsTab"].active');
    const areasTab = menu.querySelector('[data-mobile-dropdown-menu-target="areasTab"].active');

    if (itemsPane && itemsPane.hidden !== true) {
      return itemsPane;
    }

    if (areasPane && areasPane.hidden !== true) {
      return areasPane;
    }

    return itemsPane || areasPane;
  }

  isVisible(element) {
    return element.offsetParent !== null;
  }

  toggleResults(e) {
    const menu = this.containerTarget.getElementsByClassName('dropdown-menu')[0];
    if (!menu) {
      return;
    }

    const legacyItems = [...menu.getElementsByClassName('dropdown-item')];
    const newItems = [...menu.querySelectorAll('a.item, button.item')];
    const allItems = legacyItems.concat(newItems);

    if (e.relatedTarget &&
        (allItems.indexOf(e.relatedTarget) >= 0 || e.relatedTarget == this.queryTarget)) {
      this.showResults();
    } else {
      this.hideResults();
    }
  }
  
  handleFocusOut(e) {
    if (window.innerWidth < this.MOBILE_BREAKPOINT) {
      return;
    }
    this.toggleResults(e);
  }

  showResults() {
    if (this.data.get('hide-class')) {
      this.containerTarget.classList.remove(this.data.get('hide-class'));
    } else {
      const dropdownMenu = this.containerTarget.getElementsByClassName('dropdown-menu')[0];
      if (dropdownMenu) {
        dropdownMenu.classList.add('show');
        document.body.classList.add('search-dropdown-show');
      }
    }
  }

  hideResults() {
    const dropdownMenu = this.containerTarget.getElementsByClassName('dropdown-menu')[0];
    if (dropdownMenu) {
      document.body.classList.remove('search-dropdown-show');
      setTimeout(() => {
        dropdownMenu.classList.remove('show');
      }, 200);
    }
  }

  close(e) {
    setTimeout(() => {
      this.containerTarget.classList.add(this.data.get('hide-class'));
    }, 200);
  }
}

export default AutocompleteController;
